/* eslint-disable */
import querystring from 'querystring';
import http from 'http';
import { isEmpty, get, trademarkFriendlyString, makeSlug } from '../utils';
import { isConstructedTrim, getURLNCode } from '../redux/selectors/helios';

/**
 * Service call to retrieve all model lines of a specific brand from DealerCenter
 * @param state - The Redux store
 */
export const getAllModelLines = (state) => {
  const config = state.config;
  const env = state.environment;
  const channel = env.BRAND_CHANNEL;
  const lang = env.LANG;
  const url = `${config.brandPath}${config.api.getAllModelLines}/${channel}/${lang}`;

  return new Promise((resolve, reject) => {
    // just resolve if we already have models loaded.
    const models = get(state, 'modellines.models');
    if (!isEmpty(models)) {
      resolve(models);
      return;
    }
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          reject();
        }
        return response.json();
      })
      .then((result) => {
        const modelCollection = result.map(model => ({
          ...model,
          tradeMarkName: trademarkFriendlyString(model.modelLineSEO),
          slug: makeSlug(model.modelLineFullName)
        }));
        resolve(modelCollection);
      })
      .catch((error) => {
        reject(error)
      });
  });
};

/**
 * Once the user has selected a model this call will retrieve all trims
 * for that model line.
 * @param state - the Redux store
 * @param modelLineCode - The Code of the selected model line
 */
export const getTrimsByModelLineCode = (state, modelLineCode) => {
  const config = state.config;
  const env = state.environment;
  const heliosAdd = env.VENDOR === 'helios' ? 'partner=helios&gridAll=true' : '';
  const channel = env.BRAND_CHANNEL;
  const lang = env.LANG;
  const url = `${config.brandPath}${config.api.getModelTrims}/${channel}/${lang}/${modelLineCode}?${heliosAdd}`;

  return new Promise((resolve, reject) => {
    const trimInfo = get(state, `trims.trims.${modelLineCode}`);
    if (!isEmpty(trimInfo)) {
      resolve(trimInfo);
      return;
    }
    getAllModelLines(state).then(() => {
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            reject();
          }
          return response.json();
        })
        .then((result) => {
          const trimCollection = result.map(t => ({
            ...t,
            slug: makeSlug(t.mktcpoyFullName)
          }));
          resolve(trimCollection);
        })
        .catch((error) => {
          reject(error)
        });
    });
  });
};

/**
 * Service call to retrieve information on the Dealer Media Area
 * based on the users zip code
 * @param state - The Redux store
 * @param zipCode
 */
export const getDMADetails = (state, zipCode) => {
  const config = state.config;
  const url = `${config.brandPath}${config.api.getDMA}/${zipCode}`;
  return new Promise((resolve, reject) => {
    if (!zipCode) {
      reject();
      return;
    }
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          reject();
        }
        return response.json();
      })
      .then((result) => {
        resolve(result);
      })
      .catch((error) => {
        reject(error)
      });
  })
};

/**
 * Need to determine if we're coming from the Helios Configurator link out
 * and return the Total PRice for calculations if we are
 */
const heliosConfigTotalPrice = (state) => {
  // Must be on the Helios Build to use total price
  const environment = state.environment;

  // Value comes from URL to preserve if user refreshes page
  const totalPriceValue = parseInt(state.routing.locationBeforeTransitions.query.totalPrice, 10);

  // Either referref or destination must be set if we've come from Configurator
  const referrerURL = state.routing.locationBeforeTransitions.query.referrerURL;
  const destinationURL = state.routing.locationBeforeTransitions.query.destinationURL;
  const linkedFromConfig = environment.VENDOR === 'helios' && (referrerURL || destinationURL);

  return linkedFromConfig && totalPriceValue ? totalPriceValue : false;
};

/**
 * New Dealer center call to retrieve offer information from Dealer Center
 * This new call retrieves all information to be displayed rather than having
 * us calculate it on the front end
 * @param state - The Redux store
 */

export const getBestOfferOptions = (state) => {
  const config = state.config;
  const environment = state.environment;
  const modelLineCode = state.navigation.modelLineCode;
  const totalPrice = heliosConfigTotalPrice(state);
  const nCode = isConstructedTrim(state) ? getURLNCode(state) : state.navigation.nCode;
  let year = '';
  state.modellines.models.map((modelObject) => {
    if (modelObject.modelLineCode === modelLineCode) {
      year = modelObject.year;
    }
    return false;
  });
  const body = JSON.stringify({
    lang: environment.LANG,
    channelCode: environment.BRAND_CHANNEL,
    zipCode: state.dma.zipCode,
    vehicleCode: modelLineCode,
    nCode,
    year,
    userCreditRating: state.userAmounts.creditRating,
    downPayment: state.userAmounts.downPayment,
    tradeIn: state.userAmounts.tradeIn,
    totalPrice
  });
  const url = `${config.brandPath}${config.api.getBestOffers}`;
  return new Promise((resolve, reject) => {
    fetch(url,
      {
        method: 'POST',
        body
      }
    )
      .then((response) => {
        if (!response.ok) {
          reject();
        }
        return response.json();
      }).then((result) => {
        if (result.hasValidData !== true) {
          reject();
          return;
        }
        resolve({
          options: result,
        });
      })
      .catch((error) => {
        reject(error)
      });
  });
};

const filterNonContactableDealers= dealers => dealers.filter(dealer => dealer.isDealerContactable === "true");

/**
 * Function to load in a new group of dealers when a user updates or changes
 * their Zip code in the Dealer Zip Code Search Form
 * @param {*} state
 * @param {*} zip
 */
export const getDealersByZip = (state, zip) => {
  const config = state.config;
  const channelCode = state.environment.BRAND_CHANNEL;
  const zipCode = zip ? zip : state.dma.zipCode;
  const url = `${config.brandPath}${config.api.getDealersByZip}?channelCode=${channelCode}&zipCode=${zipCode}`;

  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          reject();
        }
        return response.json();
      }).then((result) => {
        resolve(filterNonContactableDealers(result));
      })
      .catch((error) => {
        reject(error)
      });
  });
}

/**
 * Gives us a Lead Source ID based on which brand/language we are on
 * @param {*} brand
 * @param {*} lang
 */
const leadSourceId = (brand, lang) => {
  if (brand === 'in') {
    return '10302';
  }
  return lang === 'es' ? '425' : '424';
}

/**
 * Builds the Object to be passed on to leads for Request a Quote
 * @param {*} data
 */
const raqObject = (data) => ({
  dealerId: data.dealer.dealerId,
  comments: data.customer.comments,
  firstName: data.customer.firstName,
  lastName: data.customer.lastName,
  emailAddress: data.customer.email,
  state: data.customer.state,
  city: data.customer.city,
  optIn: data.customer.optIn,
  year: data.vehicle.year,
  zipPostalCode: data.customer.postalCode,
  areaCode: data.customer.phone ? data.customer.phone.slice(0, 3) : '',
  homePhonePrefix: data.customer.phone ? data.customer.phone.slice(3, 6) : '',
  homePhoneSuffix: data.customer.phone ? data.customer.phone.slice(6, 10) : '',
  addressLine1: data.customer.streetNumber,
  make: data.vehicle.make,
  model: data.vehicle.model,
  trimDescription: data.vehicle.trimDescription,
  typeCd: 'buy',
  requestType: 'RAQ',
  confirm: true,
  leadSourceWebSiteId: leadSourceId(data.vehicle.make, data.customer.language),
  leadVehiclePreference: 1

});

/**
 * Builds the Object to be passed on to The Handraiser service
 * of DealerCenter
 * @param {*} data
 */
export const handraiserObject = (data) => ({
  firstName: data.customer.firstName,
  lastName: data.customer.lastName,
  addressLine1: data.customer.streetNumber,
  emailAddress: data.customer.email,
  emailAddressConfirm: data.customer.email,
  phone: data.customer.phone,
  zipCode: data.customer.postalCode,
  leadSourceId: leadSourceId(data.vehicle.make, data.customer.language),
  make: data.vehicle.make,
  model: data.vehicle.model,
  year: data.vehicle.year,
  language: data.customer.language,
  state: data.customer.state,
  city: data.customer.city,
  optinSubType: 'MARITZTYPE1',
  vehicleCode: data.vehicle.vehicleCode,
  visitorId: data.customer.visitorId

  // // This is the optinType, this needs to be changed as it does not have to be a vehicle code
  // String planOfPurchase
  // String title
});

/**
 * Calls to create Handraiser and The raqObject functions to prepare a leadss submission based on
 * a users input into the Leads Form
 * @param {*} data
 */
export const createLeadsSubmission = async data => {
  createHandraiserSubmission(data);
  return new Promise((resolve, reject) => {
    const leadValues = raqObject(data);
    const formData = querystring.stringify(leadValues);
    const contentLength = formData.length;

    const options = {
      rejectUnauthorized: false,
      host: data.host,
      path: '/dealercenter/rest/lead/RAQ',
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Content-Length': contentLength
      }
    };

    let req = http.request(options, res => {
      var result = '';
      res.on('data', chunk => {
        result += chunk;
      });

      res.on('end', () => {
        try {
          let resultJson = JSON.parse(result);
          // zip code error
          if (resultJson[0] && resultJson[0].error && resultJson[0].error.field === 'zipPostalCode') {
            reject('zipCodeMismatchError');
          } else {
            resolve(resultJson);
          }
        } catch (e) {
          // Likely dealer center is down or ip range not whitelisted and access is being denied.
          console.error(e);
          reject('Error submitting leads information to dealer center. (UNKNOWN ERROR)');
        }
      });

      res.on('error', e => {
        console.error(e);
        reject('Error submitting leads information. (RESPONSE)');
      });
    });

    req.on('error', e => {
      console.error(e);
      reject('Error submitting leads information. (DC REQUEST)');
    });

    req.write(formData);
    req.end();
  });
};

/**
 * Submitts a users data to the Dealercenter Handraiser service if the user has chosen to
 * opt into the service
 * @param {*} data
 */
const createHandraiserSubmission = async data => {
  return new Promise((resolve, reject) => {
    if (!data.customer.emailOptIn) {
      console.log('*** SUBSCRIPTION REJECTED, SKIPPING SUBMISSION ***'); //eslint-disable-line
      resolve(data);
      return;
    }

    let handraiserValues = handraiserObject(data);
    let formData = querystring.stringify(handraiserValues);
    const contentLength = formData.length;

    const options = {
      rejectUnauthorized: false,
      host: data.host,
      path: '/dealercenter/rest/signUp',
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Content-Length': contentLength
      }
    };

    let req = http.request(options, res => {
      var result = '';

      res.on('data', chunk => {
        result += chunk;
      });

      res.on('end', () => {
        try {
          let resultJson = JSON.parse(result);
          let handraiserId = get(resultJson, 'mobilehandraiser.id');

          // zip code error
          if (resultJson[0] && resultJson[0].error && resultJson[0].error.field === 'zipCode') {
            reject('zipCodeMismatchError');
          }

          // handrasier ID error
          if (typeof handraiserId === 'undefined') {
            reject('Error submitting handraiser information to dealer center. (SERVER ERROR)');
          } else {
            resolve(resultJson);
          }
        } catch (e) {
          // Likely dealer center is down or ip range not whitelisted and access is being denied.
          console.error(e);
          reject('Error submitting handraiser information to dealer center. (UNKNOWN ERROR)');
        }
      });

      res.on('error', e => {
        console.error(e);
        reject('Error submitting handraiser information. (RESPONSE)');
      });
    });

    req.on('error', e => {
      console.error(e);
      reject('Error submitting handraiser information. (DC REQUEST)');
    });

    req.write(formData);
    req.end();
  });
}

/* eslint-enable */




/** WEBPACK FOOTER **
 ** ./src/services/index.js
 **/